import React from 'react';
import styled from 'styled-components';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { DeviceType, DisplayAlignment, ProjectElementType } from '../../shared/enum/EnumRepository';
import { useProjectElementActions } from '../../shared/hooks/useApi/useProjectElement';  
import { ProjectElementData } from '../../shared/dto/EntityRepository';
import Work from './Work';
import WorkMobile from './mobile/WorkMobile';
import Detail from './Detail';
import DetailMobile from './mobile/DetailMobile';
import Document from './Document';
import DocumentMobile from './mobile/DocumentMobile';
import PdfAsset from './PdfAsset';
import PdfAssetMobile from './mobile/PdfAssetMobile';
import Text from './Text';
import TextMobile from './mobile/TextMobile';
import TextBox from './TextBox';
import TextBoxMobile from './mobile/TextBoxMobile';
import Divider from '../../shared/Divider';
import Formation from './Formation';
import FormationMobile from './mobile/FormationMobile';
import { useDeviceStore } from '../../shared/store/deviceStore';
import { useProjectStore } from '../../shared/store/projectStore';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import deleteCloseIcon from '../../asset/icon/delete_close.png';
import deleteOpenIcon from '../../asset/icon/delete_open.png';

export interface ProjectElementProps {
  data: ProjectElementData;
  priority?: boolean;
}

const ProjectElement: React.FC<ProjectElementProps> = ({ data, priority = false }) => {
  const { isEditMode } = useEditMode();
  const projectId = useProjectStore((s) => s.project?.id);
  const { deleteProjectElement } = useProjectElementActions();
  const { deviceType } = useDeviceStore();

  if (!projectId) return null;


  const contentRouter = () => {
    switch (data.projectElementType) {
      case ProjectElementType.WORK:
        return <Work peId={data.id} alignment={data.displayAlignment} displaySize={data.displaySize} infoType={data.infoType} data={data.work} priority={priority} />;
      case ProjectElementType.DETAIL:
        return <Detail peId={data.id} alignment={data.displayAlignment} displaySize={data.displaySize} infoType={data.infoType} data={data.workDetail} priority={priority} />;
      case ProjectElementType.TEXT:
        return <Text peId={data.id} alignment={data.displayAlignment} displaySize={data.displaySize} text={data.text} selectedTextBlocks={data.selectedTextBlocks} />;
      case ProjectElementType.TEXTBOX:
        return <TextBox peId={data.id} alignment={data.textAlignment} data={data.textBox} />;
      case ProjectElementType.DOCUMENT:
        return <Document peId={data.id} alignment={data.displayAlignment} displaySize={data.displaySize} infoType={data.infoType} data={data.document} priority={priority} />;
      case ProjectElementType.PDF:
        return <PdfAsset peId={data.id} alignment={data.displayAlignment} data={data.pdfAsset} priority={priority} />;
      case ProjectElementType.DIVIDER:
        return (
          <DividerWrapper $isMobile={false}>
            <Divider dividerType={data.dividerType} />
          </DividerWrapper>
        );
      case ProjectElementType.FORMATION:
        return (
          <Formation
            peId={data.id}
            data={data.formation}
            displaySize={data.displaySize}
            infoType={data.infoType}
          />
        );
      default:
        return null;
    }
  }
  /**
   * 모바일 환경에서는 TextBox를 제외한 모든 요소를 중앙 정렬로 보여준다.
   */
  const mobileContentRouter = () => {
    switch (data.projectElementType) {
      case ProjectElementType.WORK:
        return <WorkMobile peId={data.id} alignment={DisplayAlignment.CENTER} displaySize={data.displaySize} infoType={data.infoType} data={data.work} priority={priority} />;
      case ProjectElementType.DETAIL:
        return <DetailMobile peId={data.id} alignment={DisplayAlignment.CENTER} displaySize={data.displaySize} infoType={data.infoType} data={data.workDetail} priority={priority} />;
      case ProjectElementType.TEXT:
        return <TextMobile peId={data.id} text={data.text} selectedTextBlocks={data.selectedTextBlocks} />;
      case ProjectElementType.TEXTBOX:
        return <TextBoxMobile peId={data.id} alignment={data.textAlignment} data={data.textBox} />;
      case ProjectElementType.DOCUMENT:
        return <DocumentMobile peId={data.id} alignment={DisplayAlignment.CENTER} displaySize={data.displaySize} infoType={data.infoType} data={data.document} priority={priority} />;
      case ProjectElementType.PDF:
        return <PdfAssetMobile peId={data.id} alignment={data.displayAlignment} data={data.pdfAsset} />;
      case ProjectElementType.DIVIDER:
        return (
          <DividerWrapper $isMobile={true}>
            <Divider dividerType={data.dividerType} />
          </DividerWrapper>
        );
      case ProjectElementType.FORMATION:
        return (
          <FormationMobile
            peId={data.id}
            data={data.formation}
            displaySize={data.displaySize}
            infoType={data.infoType}
          />
        );
      default:
        return null;
    }
  }

  const handleDelete = async () => {
    try {
      await deleteProjectElement({ projectElementId: data.id });
    } catch (err) {
    }
  }

  return (
    <ProjectElementListWrapper
      $elementType={data.projectElementType}
      data-has-changed={data.hasChanged ? "true" : "false"}    // 편집 모드 진입 시 변경사항이 있으면 스크롤
    >
      {deviceType === DeviceType.MOBILE ? mobileContentRouter() : contentRouter()}
      {isEditMode &&
        <BtnContainer $isMobile={deviceType === DeviceType.MOBILE}>
          <UniversalIconBtn 
            size="fit" 
            border={false} 
            backgroundImg={deleteCloseIcon} 
            backgroundImgHover={deleteOpenIcon}
            onClick={handleDelete} />
        </BtnContainer>
      }
    </ProjectElementListWrapper>
  );
}

const ProjectElementListWrapper = styled.div<{ $elementType: ProjectElementType }>`
  position: relative;
  width: 100%;
  padding-bottom: 70px;
  margin-bottom: ${({ $elementType }) => {
    /* 각 Element를 순수하게 남기기 위해 여기서 설정 */
    switch ($elementType) {
      case ProjectElementType.WORK:
      case ProjectElementType.DETAIL:
      case ProjectElementType.DOCUMENT:
        return 'calc(16vh)';
      case ProjectElementType.TEXT:
      case ProjectElementType.TEXTBOX:
        return 'calc(10vh)';
      case ProjectElementType.PDF:
        return 'calc(6vh)';
      case ProjectElementType.DIVIDER:
        return 'calc(4vh)';
      case ProjectElementType.FORMATION:
        return 'calc(14vh)';
      default:
        return 'calc(12vh)';
    }
  }};
`;

const BtnContainer = styled.div<{ $isMobile: boolean }>`
  position: absolute;
  right: ${({ $isMobile }) => $isMobile ? 'clamp(18px, 5vw, 24px)' : 'calc(8vw)'};
  bottom: 0;
  width: fit-content;
  display: flex;
  justify-content: flex-end;
  gap: clamp(4px, 1.2vw, 6px);
`

const DividerWrapper = styled.div<{ $isMobile: boolean }>`
  padding: 0 ${({ $isMobile }) => $isMobile ? 'clamp(18px, 5vw, 24px)' : '8vw'};
`;

export default React.memo(ProjectElement);
